import { useRef, useState, useCallback } from "react";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@design-lab/shared/lib";

export type SizePreset = {
  id: string;
  name: string;
  image: string;
};

export type PresetsListProps = {
  selectedPresets?: string[];
  onPresetSelect?: (presetId: string) => void;
};

const presets: SizePreset[] = [
  {
    id: "custom",
    name: "Custom",
    image: "https://jiffy.imgix.net/gen/transfers-lab/transfer-preset-full-front.png"
  },
  {
    id: "full-front",
    name: "Full Front",
    image: "https://jiffy.imgix.net/gen/transfers-lab/transfer-preset-full-front.png"
  },
  {
    id: "full-back",
    name: "Full Back",
    image: "https://jiffy.imgix.net/gen/transfers-lab/transfer-preset-full-back.png"
  },
  {
    id: "left-chest",
    name: "Left Chest",
    image: "https://jiffy.imgix.net/gen/transfers-lab/transfer-preset-left-chest.png"
  },
  {
    id: "sleeve",
    name: "Sleeve",
    image: "https://jiffy.imgix.net/gen/transfers-lab/transfer-preset-sleeve.png"
  },
  {
    id: "back-collar",
    name: "Back Collar",
    image: "https://jiffy.imgix.net/gen/transfers-lab/transfer-preset-back-collar.png"
  }
];

export function PresetsList(props: PresetsListProps) {
  const { selectedPresets = [], onPresetSelect } = props;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    setShowLeftArrow(container.scrollLeft > 0);
    setShowRightArrow(container.scrollLeft < container.scrollWidth - container.clientWidth - 1);
  }, []);

  const scrollLeft = useCallback(() => {
    scrollContainerRef.current?.scrollBy({ left: -200, behavior: "smooth" });
  }, []);

  const scrollRight = useCallback(() => {
    scrollContainerRef.current?.scrollBy({ left: 200, behavior: "smooth" });
  }, []);

  return (
    <div className="group relative">
      {/* Left navigation arrow */}
      {showLeftArrow && (
        <button
          type="button"
          onClick={scrollLeft}
          className="absolute -left-3 top-1/2 z-10 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-md hover:shadow-lg group-hover:flex"
          aria-label="Scroll left"
          data-testid="preset-scroll-left"
        >
          <ChevronLeft className="h-5 w-5 text-gray-600" />
        </button>
      )}

      {/* Right navigation arrow */}
      {showRightArrow && (
        <button
          type="button"
          onClick={scrollRight}
          className="absolute -right-3 top-1/2 z-10 hidden h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-md hover:shadow-lg group-hover:flex"
          aria-label="Scroll right"
          data-testid="preset-scroll-right"
        >
          <ChevronRight className="h-5 w-5 text-gray-600" />
        </button>
      )}

      {/* Scrollable presets container */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex gap-3 overflow-x-auto [&::-webkit-scrollbar]:hidden"
      >
        {presets.map(preset => {
          const isSelected = selectedPresets.includes(preset.id);

          return (
            <button
              key={preset.id}
              type="button"
              onClick={() => onPresetSelect?.(preset.id)}
              className={cn(
                "relative flex h-28 w-24 flex-shrink-0 flex-col items-center justify-between rounded-lg border-2 p-2 transition-all hover:bg-gray-50",
                isSelected ? "border-blue-600 bg-blue-50" : "border-gray-200 hover:border-gray-300"
              )}
              data-testid={`preset-${preset.id}`}
              aria-pressed={isSelected}
              aria-label={`Select ${preset.name} preset`}
            >
              {/* Selection checkmark */}
              {isSelected && (
                <div className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-blue-600">
                  <Check className="h-3 w-3 text-white" strokeWidth={3} />
                </div>
              )}

              {/* Preset image */}
              <div className="flex h-20 w-full items-center justify-center overflow-hidden rounded-md">
                <img
                  src={preset.image}
                  alt={preset.name}
                  className="h-full w-full object-contain"
                  loading="lazy"
                />
              </div>

              {/* Preset name */}
              <span className="text-[10px] font-medium text-gray-900">{preset.name}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
